import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, X, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "cookieConsent";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
}

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always enabled
    analytics: false,
    preferences: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      const saved = JSON.parse(savedConsent);
      setPreferences(saved);
      applyConsent(saved);
    }
  }, []);

  const applyConsent = (prefs: CookiePreferences) => {
    // Apply cookie preferences - handled by analytics system
  };

  const handleAcceptAll = () => {
    const allPrefs = {
      essential: true,
      analytics: true,
      preferences: true,
    };
    setPreferences(allPrefs);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allPrefs));
    applyConsent(allPrefs);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const minimalPrefs = {
      essential: true,
      analytics: false,
      preferences: false,
    };
    setPreferences(minimalPrefs);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(minimalPrefs));
    applyConsent(minimalPrefs);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    applyConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === "essential") return; // Essential cookies cannot be disabled
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom">
      <Card className="max-w-4xl mx-auto shadow-2xl border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Cookie className="h-6 w-6 text-primary" />
              <div>
                <CardTitle className="text-lg">Cookie Preferences</CardTitle>
                <CardDescription>We use cookies to enhance your experience</CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRejectAll}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            We use essential cookies for authentication and security. With your consent, we also use analytics cookies to improve our service. You can customize your preferences below.
          </p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handleAcceptAll} className="flex-1 sm:flex-none">
              Accept All
            </Button>
            <Button onClick={handleRejectAll} variant="outline" className="flex-1 sm:flex-none">
              Reject All
            </Button>
            
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
              <DialogTrigger asChild>
                <Button variant="secondary" className="flex-1 sm:flex-none">
                  <Settings className="h-4 w-4 mr-2" />
                  Customize
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Cookie Settings</DialogTitle>
                  <DialogDescription>
                    Choose which cookies you want to accept
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex-1">
                      <Label htmlFor="essential" className="font-semibold">
                        Essential Cookies
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Required for authentication and core functionality
                      </p>
                    </div>
                    <Switch
                      id="essential"
                      checked={preferences.essential}
                      disabled
                    />
                  </div>

                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex-1">
                      <Label htmlFor="analytics" className="font-semibold">
                        Analytics Cookies
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Help us understand usage and improve the service
                      </p>
                    </div>
                    <Switch
                      id="analytics"
                      checked={preferences.analytics}
                      onCheckedChange={() => handleToggle("analytics")}
                    />
                  </div>

                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex-1">
                      <Label htmlFor="preferences" className="font-semibold">
                        Preference Cookies
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Remember your settings and preferences
                      </p>
                    </div>
                    <Switch
                      id="preferences"
                      checked={preferences.preferences}
                      onCheckedChange={() => handleToggle("preferences")}
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSavePreferences} className="flex-1">
                    Save Preferences
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <p className="text-xs text-muted-foreground">
            By clicking "Accept All", you consent to our use of cookies. Read our{" "}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="text-primary hover:underline">
              Terms of Service
            </Link>{" "}
            for more information.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
